import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'flowbite-react';
import moment from 'moment';
import { Change, Checklist, Comment, Project, ProjectLabel, User } from '../../models';
import { loadChecklists, RootState, useAppDispatch, useAppSelector } from '../../redux';
import Changes from '../../services/Changes';
import { Button } from '../Button';
import { Checklists } from '../Checklists';
import { ClientReplyPendingBadge } from '../ClientReplyPendingBadge';
import { HtmlContent } from '../HtmlContent';
import { Icon } from '../Icon';
import { Loader } from '../Loader';
import { Markdown } from '../Markdown';
import { RemoteImagesProvider } from '../RemoteImages';
import { UserAvatar } from '../UserAvatar';
import {
    handleChangeMiddleClick,
    preventMiddleClickAutoscroll,
} from '../../utils/openChangeInNewBrowserTab';

interface PinnedChangePanelProps {
    changeId: string;
    project: Project;
    onUnpin: () => void;
    onOpen: (changeId: string) => void;
    /** Extra classes for width / layout when several pins sit side by side. */
    className?: string;
}

function PinnedChangePanel(props: PinnedChangePanelProps) {
    const { t, i18n } = useTranslation();
    const { changeId, project, onUnpin, onOpen, className } = props;
    const dispatch = useAppDispatch();
    const [change, setChange] = useState<Change | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const checklists = useAppSelector((state: RootState) =>
        (state.checklists.checklists || []).filter((list: Checklist) => list.change?._id === changeId)
    );

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        Changes.get(changeId)
            .then((json: AnyObj) => {
                if (cancelled)
                    return;
                setChange(json.change as Change);
            })
            .catch((err: Error) => {
                if (cancelled)
                    return;
                setError(err?.message || t('changes.pin_load_error') || '');
                setChange(null);
            })
            .finally(() => {
                if (!cancelled)
                    setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [changeId, t]);

    useEffect(() => {
        dispatch(loadChecklists({ change: changeId }));
    }, [dispatch, changeId]);

    const descriptionHtml = change?.localizedDescription?.(i18n.language) || '';
    const technicalNotes = change?.technicalContext?.legacyNotes?.trim() || '';
    const status = change?.progress?.status;
    const statusLabel = status
        ? (t(`changes.progress_status_type.${status}`) || status)
        : '';

    const labels = useMemo(() => {
        if (!change?.labelIds?.length || !project?.labels?.length)
            return [] as ProjectLabel[];
        const byId = new Map((project.labels || []).map((label: ProjectLabel) => [label._id, label]));
        return change.labelIds
            .map((id: string) => byId.get(id))
            .filter(Boolean) as ProjectLabel[];
    }, [change?.labelIds, project?.labels]);

    const comments = useMemo(() => {
        if (!change?.comments?.length)
            return [] as Comment[];
        return [...change.comments]
            .map((item: AnyObj) => item instanceof Comment ? item : new Comment(item))
            .sort((a: Comment, b: Comment) => {
                const aAt = moment(a.createdAt).valueOf();
                const bAt = moment(b.createdAt).valueOf();
                return bAt - aAt;
            });
    }, [change?.comments]);

    const htmlSources = useMemo(() => {
        const sources = [descriptionHtml];
        comments.forEach((comment: Comment) => {
            if (comment.message)
                sources.push(comment.message);
        });
        return sources;
    }, [descriptionHtml, comments]);

    return (
        <aside
            className={`flex h-full flex-col border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl ${className || 'w-full max-w-lg'}`}
            aria-label={t('changes.pinned_panel') || ''}
        >
            <div
                className="flex shrink-0 cursor-default items-center justify-between gap-2 border-b border-gray-200 dark:border-gray-700 bg-amber-50 px-3 py-2"
                title={t('changes.unpin_middle_click') || t('changes.unpin') || ''}
                onMouseDown={(event) => {
                    // Prevent middle-click autoscroll.
                    if (event.button === 1)
                        event.preventDefault();
                }}
                onAuxClick={(event) => {
                    if (event.button === 1) {
                        event.preventDefault();
                        onUnpin();
                    }
                }}
            >
                <div className="flex min-w-0 items-center gap-2">
                    <Icon type="pin" size={4} color="amber-700" />
                    <span className="truncate text-xs font-semibold uppercase tracking-wide text-amber-900">
                        {t('changes.pinned_panel')}
                    </span>
                </div>
                <button
                    type="button"
                    className="inline-flex items-center rounded p-1.5 text-amber-800 hover:bg-amber-100"
                    title={t('changes.unpin') || ''}
                    aria-label={t('changes.unpin') || ''}
                    onClick={onUnpin}
                >
                    <Icon type="close" size={4} color="amber-800" />
                </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
                {loading && (
                    <div className="p-4">
                        <Loader />
                    </div>
                )}
                {!loading && error && (
                    <p className="p-4 text-sm text-red-700">{error}</p>
                )}
                {!loading && change && (
                    <RemoteImagesProvider changeId={change._id} htmlSources={htmlSources}>
                        <div className="space-y-4 p-4">
                            <div className="space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge color="info" className="w-fit">
                                        <span className="inline-flex items-center gap-1">
                                            <Icon type={change.iconName?.() || 'feature'} size={3} />
                                            {change.slugName?.() || `#${change.slug}`}
                                        </span>
                                    </Badge>
                                    {statusLabel && (
                                        <Badge color="indigo" className="w-fit">
                                            {statusLabel}
                                        </Badge>
                                    )}
                                    {change.type && (
                                        <Badge color="gray" className="w-fit">
                                            {t(`projects.${change.type}`) || change.type}
                                        </Badge>
                                    )}
                                    <ClientReplyPendingBadge
                                        change={change}
                                        dismissable
                                        onDismissed={() => setChange(new Change({
                                            ...change,
                                            clientReplyPending: false,
                                        }))}
                                    />
                                </div>
                                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                    {change.localizedTitle?.(i18n.language) || '—'}
                                </h2>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-gray-300">
                                    {change.assignee && (
                                        <span className="inline-flex items-center gap-1.5">
                                            <UserAvatar user={change.assignee as User} size={5} hideInitials />
                                            <span>{(change.assignee as User).fullname?.() || (change.assignee as User).email}</span>
                                        </span>
                                    )}
                                    {typeof change.estimate === 'number' && change.estimate > 0 && (
                                        <span className="inline-flex items-center gap-1">
                                            <Icon type="estimated" size={3} color="gray-500" />
                                            {change.estimate} h
                                        </span>
                                    )}
                                    {typeof change.importance === 'number' && change.importance > 0 && (
                                        <span className="inline-flex items-center gap-1">
                                            <Icon type="importance" size={3} color="gray-500" />
                                            {change.importance}
                                        </span>
                                    )}
                                    {change.deadline && (
                                        <span className="inline-flex items-center gap-1">
                                            <Icon type="date" size={3} color="gray-500" />
                                            {moment(change.deadline).format('DD.MM.YYYY')}
                                        </span>
                                    )}
                                    {typeof change.priority === 'number' && (
                                        <span className="inline-flex items-center gap-1">
                                            <Icon type="priority" size={3} color="gray-500" />
                                            {change.priority}
                                        </span>
                                    )}
                                </div>
                                {labels.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                        {labels.map((label: ProjectLabel) => (
                                            <span
                                                key={label._id}
                                                className="rounded px-1.5 py-0.5 text-[11px] font-medium"
                                                style={{
                                                    backgroundColor: label.color,
                                                    color: ProjectLabel.textColor(label.color),
                                                }}
                                            >
                                                {label.name}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <section className="space-y-2 border-t border-gray-100 dark:border-gray-700 pt-3">
                                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    {t('changes.pin_section_description')}
                                </h3>
                                {descriptionHtml ? (
                                    <HtmlContent
                                        html={descriptionHtml}
                                        className="prose prose-sm max-w-none text-gray-700 dark:text-gray-200"
                                        projectId={project?._id}
                                    />
                                ) : (
                                    <p className="text-sm italic text-gray-400">
                                        {t('changes.no_description')}
                                    </p>
                                )}
                            </section>

                            {technicalNotes && (
                                <section className="space-y-2 border-t border-gray-100 dark:border-gray-700 pt-3">
                                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        {t('changes.pin_section_technical_notes')}
                                    </h3>
                                    <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3">
                                        <Markdown content={technicalNotes} />
                                    </div>
                                </section>
                            )}

                            {checklists.length > 0 && (
                                <section className="space-y-2 border-t border-gray-100 dark:border-gray-700 pt-3">
                                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        {t('changes.pin_section_checklists')}
                                        {` (${checklists.length})`}
                                    </h3>
                                    <Checklists change={change._id} />
                                </section>
                            )}

                            <section className="space-y-2 border-t border-gray-100 dark:border-gray-700 pt-3">
                                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    {t('changes.pin_section_comments')}
                                    {comments.length > 0 ? ` (${comments.length})` : ''}
                                </h3>
                                {comments.length === 0 && (
                                    <p className="text-sm italic text-gray-400">
                                        {t('changes.pin_no_comments')}
                                    </p>
                                )}
                                <div className="space-y-3">
                                    {comments.map((comment: Comment) => (
                                        <article
                                            key={comment._id || `${comment.createdAt}-${comment.authorName?.()}`}
                                            className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3"
                                        >
                                            <div className="mb-2 flex items-center justify-between gap-2">
                                                <div className="flex min-w-0 items-center gap-2">
                                                    <UserAvatar user={comment.author as User} size={5} hideInitials />
                                                    <span className="truncate text-xs font-semibold text-gray-800 dark:text-gray-100">
                                                        {comment.authorName?.() || '—'}
                                                    </span>
                                                </div>
                                                <time className="shrink-0 text-[11px] text-gray-500 dark:text-gray-400">
                                                    {moment(comment.createdAt).format('DD.MM.YYYY HH:mm')}
                                                </time>
                                            </div>
                                            {comment.message ? (
                                                <HtmlContent
                                                    html={comment.message}
                                                    className="prose prose-sm max-w-none text-gray-700 dark:text-gray-200"
                                                    projectId={project?._id}
                                                />
                                            ) : (
                                                <p className="text-sm italic text-gray-400">—</p>
                                            )}
                                        </article>
                                    ))}
                                </div>
                            </section>
                        </div>
                    </RemoteImagesProvider>
                )}
            </div>

            {!loading && change && (
                <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 p-3">
                    <div
                        onMouseDown={preventMiddleClickAutoscroll}
                        onAuxClick={(event) => handleChangeMiddleClick(event, change, project)}
                    >
                        <Button
                            title={t('changes.open_pinned') || ''}
                            color="secondary"
                            className="w-full"
                            onClick={() => onOpen(change._id)}
                        />
                    </div>
                </div>
            )}
        </aside>
    );
}

export default PinnedChangePanel;
